import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { useMemo } from "react";
import { IOrder } from "types/Order/Order";
import { IPayment } from "types/Payment";
//
const local = StyleSheet.create({
  container: {
    marginHorizontal: 50,
    marginVertical: 12,
    padding: "10px 15px",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#d9d9d9",
    backgroundColor: "#ffffff",
  },
  paymentItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  paymentSubItem: {
    paddingLeft: 16,
    marginBottom: 3,
  },
  label: {
    color: "#666",
    fontSize: 11,
  },
  amount: {
    fontSize: 12,
  },
  smallText: {
    fontSize: 10,
    color: "#666",
  },
  divider: {
    width: "100%",
    borderTopWidth: 0.5,
    borderTopColor: "#d9d9d9",
    marginVertical: 6,
  },
  totalTitle: {
    color: "#4f46e5",
    fontSize: 12,
  },
  totalAmount: {
    color: "#4f46e5",
    fontSize: 12,
  },
  installmentsLine: {
    color: "#4f46e5",
    fontSize: 10,
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 14,
    marginBottom: 10,
  },
});

interface IPaymentSummarySectionProps {
  order: IOrder;
  cardInfo?: { brand: string; last4: string };
}

export function PaymentSummarySection({
  order,
  cardInfo,
}: IPaymentSummarySectionProps) {
  const paymentsInfo = useMemo(() => {
    const allPayments: IPayment[] = order?.payments || [];
    const sortedAllPayments = [...allPayments].sort((a, b) => {
      const dateA = new Date(a.createdAt || new Date(0)).getTime();
      const dateB = new Date(b.createdAt || new Date(0)).getTime();
      return dateA - dateB;
    });
    const allPaidPayments = sortedAllPayments.filter(
      (p) => p.status === "Paid"
    );
    const allRefundedPayments = sortedAllPayments.filter(
      (p) => p.status === "Refunded"
    );
    const totalPaid = allPaidPayments.reduce(
      (sum, p) => sum + (p.amountInCents || 0),
      0
    );
    const totalRefunded = allRefundedPayments.reduce(
      (sum, p) => sum + (p.amountInCents || 0),
      0
    );
    const isTotalAdjusted = allRefundedPayments.length > 0;
    const totalPaidAmount = totalPaid - totalRefunded;
    return {
      sortedAllPayments,
      isTotalAdjusted,
      totalPaidAmount,
      allPaidPayments,
      allRefundedPayments,
      totalPaid,
      totalRefunded,
    };
  }, [order]);

  const activePaymentPlan = useMemo(
    () => order?.paymentPlan?.find((plan) => plan.active),
    [order]
  );

  const showPaymentPlanInfo = !!activePaymentPlan?.installments?.length;

  const paymentPlanInfo = useMemo(() => {
    if (!activePaymentPlan) return null;
    const installments = [...(activePaymentPlan.installments || [])].sort(
      (a, b) => new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
    );
    const nextInstallment = installments.find((i) => !i.paymentId);
    const nextInstallmentDate = nextInstallment?.dateDue
      ? parseDate(nextInstallment.dateDue, false, true)
      : "N/A";
    const remainingInstallmentsCount = installments.filter(
      (i) => !i.paymentId
    ).length;
    const showInstallmentsInfo =
      !!activePaymentPlan.remainingAmountInCents &&
      !!activePaymentPlan.paymentPlanDuration;
    return {
      ...activePaymentPlan,
      nextInstallment,
      nextInstallmentDate,
      remainingInstallmentsCount,
      showInstallmentsInfo,
    };
  }, [activePaymentPlan]);

  const firstPayment = paymentsInfo.allPaidPayments[0];
  const firstRefund = paymentsInfo.allRefundedPayments[0];

  const formatDate = (date: string | Date | undefined): string =>
    date ? parseDate(date, false, true) : "N/A";

  return (
    <View style={local.container}>
      <Text style={local.sectionTitle}>Payment Summary</Text>

      <View style={local.paymentItem}>
        <Text style={local.label}>
          {paymentsInfo.allPaidPayments.length <= 1
            ? `Total Paid on ${formatDate(firstPayment?.createdAt)}`
            : "Total Paid:"}
        </Text>
        <Text style={local.amount}>
          {parsePrice(centsToDollars(paymentsInfo.totalPaid))}
        </Text>
      </View>

      {paymentsInfo.allPaidPayments.length > 1 &&
        paymentsInfo.allPaidPayments.map((payment) => (
          <View
            key={payment.id ?? `${payment.createdAt}-${payment.amountInCents}`}
            style={{ ...local.paymentItem, ...local.paymentSubItem }}
          >
            <Text style={local.smallText}>
              {`${payment.status} on: ${formatDate(payment.createdAt)}`}
            </Text>
            <Text style={local.smallText}>
              {parsePrice(centsToDollars(payment.amountInCents || 0))}
            </Text>
          </View>
        ))}

      {paymentsInfo.allRefundedPayments.length > 0 && (
        <>
          <View style={local.divider} />
          <View style={local.paymentItem}>
            <Text style={local.label}>
              {paymentsInfo.allRefundedPayments.length <= 1
                ? `Total Refunded on ${formatDate(firstRefund?.createdAt)}`
                : "Total Refunded:"}
            </Text>
            <Text style={local.amount}>
              {parsePrice(centsToDollars(paymentsInfo.totalRefunded))}
            </Text>
          </View>
          {paymentsInfo.allRefundedPayments.length > 1 &&
            paymentsInfo.allRefundedPayments.map((payment) => (
              <View
                key={
                  payment.id ?? `${payment.createdAt}-${payment.amountInCents}`
                }
                style={{ ...local.paymentItem, ...local.paymentSubItem }}
              >
                <Text style={local.smallText}>
                  {`${payment.status} on: ${formatDate(payment.createdAt)}`}
                </Text>
                <Text style={local.smallText}>
                  {parsePrice(centsToDollars(payment.amountInCents || 0))}
                </Text>
              </View>
            ))}
        </>
      )}

      {paymentsInfo.sortedAllPayments.length > 1 && (
        <>
          <View style={local.divider} />
          <View style={local.paymentItem}>
            <Text style={local.totalTitle}>
              {paymentsInfo.isTotalAdjusted ? "Adjusted Total" : "Total"}
            </Text>
            <Text style={local.totalAmount}>
              {parsePrice(centsToDollars(paymentsInfo.totalPaidAmount))}
            </Text>
          </View>
        </>
      )}

      {showPaymentPlanInfo &&
        paymentPlanInfo &&
        !paymentPlanInfo.isDownPaymentOnly && (
          <View style={local.paymentItem}>
            <Text style={local.totalTitle}>Remaining:</Text>
            <Text style={local.totalAmount}>
              {parsePrice(
                centsToDollars(paymentPlanInfo.remainingAmountInCents || 0)
              )}
            </Text>
          </View>
        )}

      {showPaymentPlanInfo &&
        paymentPlanInfo?.showInstallmentsInfo &&
        !paymentPlanInfo.isDownPaymentOnly && (
          <Text style={local.installmentsLine}>
            {`${
              paymentPlanInfo.remainingInstallmentsCount
            } payments of ${parsePrice(
              centsToDollars(
                paymentPlanInfo.nextInstallment?.amountInCents || 0
              )
            )} /month   Next Due: ${paymentPlanInfo.nextInstallmentDate}`}
          </Text>
        )}

      {(cardInfo || firstPayment?.type) && (
        <View style={{ ...local.paymentItem, marginTop: 10 }}>
          <Text style={local.label}>Method:</Text>
          <Text style={{ ...local.label, color: "#0d0d0d" }}>
            {cardInfo
              ? `${
                  firstPayment?.type ?? "Card"
                } (type: ${cardInfo.brand.toUpperCase()}) ending ${
                  cardInfo.last4
                }`
              : firstPayment?.type}
          </Text>
        </View>
      )}
    </View>
  );
}
